import { useMutation, useQuery } from '@apollo/client';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Nav from 'react-bootstrap/Nav';
import Row from 'react-bootstrap/Row';
import { Tab } from 'react-bootstrap';
import { Redirect, Route, Switch, useHistory, useParams } from 'react-router-dom';
import { CREATE_CALENDAR, GET_CALENDARS } from './graphql';
import CreateModal from './components/CreateModal';
import { LoadingLogo } from '../../../devtools/components/Modal';
import { PaidCalendar } from '../../../../../__gqltypes__';
import CalendarView from './components/CalendarView';

function indexItems<T extends { id: string }>(list: T[]) {
  const indexedItems: Record<string, T> = {};

  list.forEach((item) => {
    indexedItems[item.id] = item;
  });

  return indexedItems;
}
const baseURL = '/sp3/liveops/calendars';

const CalendarsView = <T extends string>({ type }: { type: T }) => {
  const { id: calendarId } = useParams<{ id: string }>();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [calendars, setCalendars] = useState<Record<string, PaidCalendar> | null>(null);
  const { data, loading, refetch } = useQuery(GET_CALENDARS, {
    fetchPolicy: 'no-cache',
  });

  const history = useHistory();

  useEffect(() => {
    if (data) {
      setCalendars(
        indexItems(
          data?.getCalendars?.sort((a: { startDate: number }, b: { startDate: number }) => b.startDate - a.startDate)
        )
      );
    }
  }, [data]);

  const [createCalendar, { loading: createLoading }] = useMutation(CREATE_CALENDAR, {
    onCompleted: () => refetch(),
  });

  const selectCalendar = useCallback(
    (newCalendarId) => {
      if (Object.keys(calendars ?? {}).includes(newCalendarId)) {
        history.push(`${baseURL}/${newCalendarId}`);
      }
    },
    [calendars, history]
  );

  const isCalendarSelected = useCallback(() => {
    return !_.isEmpty(calendarId) && calendars && Object.values(calendars).find(({ id }) => id === calendarId);
  }, [calendarId, calendars]);

  useEffect(() => {
    if (calendarId === ':id') {
      selectCalendar('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCalendarSelected, selectCalendar, calendarId]);

  const handleCreate = (name: string, startDate: Date, endDate: Date) => {
    createCalendar({
      variables: {
        name,
        startDate,
        endDate,
      },
    })
      .then(({ data: data_ }) => {
        setShowCreateModal(false);
        refetch().then(() => history.push(`/sp3/liveops/calendars/${data_.createCalendarAdmin.id}`));
      })
      .catch(() => {
        alert('Something went wrong :(');
      });
  };

  return (
    <>
      <LoadingLogo show={loading || createLoading} />
      <CreateModal
        show={(!calendars || data?.getCalendars?.length === 0 || showCreateModal) && !loading}
        loading={loading || createLoading}
        onHide={() => setShowCreateModal(false)}
        onAdd={handleCreate}
      />
      {calendars && (
        <Tab.Container activeKey={calendarId} onSelect={(e) => selectCalendar(e)}>
          <Row className="d-flex">
            <Col>
              <h1 className="text-center">{type}</h1>
              <Button className="w-100 mb-3" variant="success" onClick={() => setShowCreateModal(true)}>
                Add a new {type}
              </Button>
              <Nav variant="pills" className="mb-3" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                {Object.values(calendars).map((item) => (
                  <Nav.Item key={item.id} style={{ width: '100%' }} className="text-center">
                    <Nav.Link eventKey={item.id}>{item.name}</Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            </Col>
            <Col className="col-10">
              <Switch>
                {calendars && (
                  <Route exact path="/sp3/liveops/calendars">
                    <Redirect to={`/sp3/liveops/calendars/${Object.keys(calendars)[0]}`} />
                  </Route>
                )}

                <Route
                  path="/sp3/liveops/calendars/:id"
                  render={({ match: _match }) => {
                    return (
                      !!data?.getCalendars?.length && (
                        <CalendarView calendar={calendars[_match.params.id]} refetch={refetch} />
                      )
                    );
                  }}
                />
              </Switch>
            </Col>
          </Row>
        </Tab.Container>
      )}
    </>
  );
};

export default CalendarsView;
